import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel, LineStyle, PointStyle } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement, GeometryElementDisplayConfig } from '../../geometrypad.model';
import type { GeometryPadElementContext } from '../element.model';
import {
  type GeometryStyleContext,
  resolveGeometryStyle,
  resolveGeometryLabelColor,
  resolveSubpartLabelOverride,
  resolveAngleLabelOverride,
  computeAngleScope,
  computeEdgeScope,
} from '../coloring';

/**
 * Default JSXGraph attributes for hidden constraint points.
 * Used by shape creators to create invisible fixed points that
 * define the geometry but are not shown to the user.
 */
export const HIDDEN_POINT_ATTRS = { visible: false, fixed: true, withLabel: false } as const;

/**
 * Common context values extracted from a GeometryPadElementContext
 * during shape creation. Returned by `initShapeCreatorContext()`.
 */
export interface ShapeCreatorContext {
  elementFactory: GeometryPadElementContext['elementFactory'];
  geometryConfig: GeometryPadElementContext['geometryConfig'];
  elementDisplay: GeometryElementDisplayConfig | undefined;
  board: ReturnType<GeometryPadElementContext['elementFactory']['getContext']>['board'];
  jsxObjects: JXGElement[];
  labels: BoundLabel[];
  styleCtx: GeometryStyleContext;
}

/**
 * Initialize common shape creator context from the element and widget context.
 *
 * Every shape creator needs the same set of values extracted from the context.
 * This helper eliminates the 8-10 line boilerplate that was duplicated across
 * all shape creators.
 */
export function initShapeCreatorContext(
  element: GeometryElement,
  context: GeometryPadElementContext,
): ShapeCreatorContext {
  const { elementFactory, geometryConfig, displayConfig } = context;
  const elementDisplay = displayConfig.elements?.[element.id];
  return {
    elementFactory,
    geometryConfig,
    elementDisplay,
    board: elementFactory.getContext().board,
    jsxObjects: [],
    labels: [],
    styleCtx: {
      elementFactory,
      shapeTheme: context.shapeTheme,
      geometryConfig,
    },
  };
}

/**
 * Build per-edge border styles for a quadrilateral shape.
 *
 * Handles label deduplication automatically: only the first edge
 * for each unique property gets a label. This naturally handles:
 * - Rectangle/Parallelogram: different properties per edge pair, dedup by property
 * - Square/Rhombus: all edges → same property, only first labeled
 * - Trapezoid: all unique properties, all labeled
 */
export function createBorderStyles(
  edgeNames: readonly string[],
  propertyForEdge: Record<string, string>,
  shapeType: string,
  element: GeometryElement,
  givenKeys: Set<string>,
  ctx: ShapeCreatorContext,
): Record<number, LineStyle> {
  const { geometryConfig, elementDisplay, styleCtx } = ctx;
  const names = (element as GeometryElement & { names?: Record<string, string> }).names;
  const shownProperties = new Set<string>();
  const borderPerEdge: Record<number, LineStyle> = {};

  for (let i = 0; i < edgeNames.length; i++) {
    const edgeName = edgeNames[i];
    const prop = propertyForEdge[edgeName];
    const labelName = names?.[prop] ?? prop;
    const showLabel = !shownProperties.has(prop);
    shownProperties.add(prop);

    const edgeStyle = resolveGeometryStyle('line', shapeType, styleCtx, elementDisplay, edgeName);
    const labelColor = resolveGeometryLabelColor(shapeType, styleCtx, elementDisplay, edgeName) as ThemeColorName;
    const globalLabel = showLabel
      ? computeEdgeScope(prop, givenKeys, geometryConfig, labelColor, labelName !== prop ? labelName : undefined)
      : false;
    borderPerEdge[i] = {
      color: edgeStyle.color,
      strokeWidth: edgeStyle.strokeWidth,
      dash: edgeStyle.dash,
      label: resolveSubpartLabelOverride(edgeStyle.label, globalLabel, labelName, labelColor),
    };
  }

  return borderPerEdge;
}

/** Standard vertex names for quadrilateral shapes. */
export const QUAD_VERTEX_NAMES = ['A', 'B', 'C', 'D'] as const;

/** Standard CCW angle winding for a 4-vertex polygon. */
export const QUAD_ANGLE_KEYS = ['alpha', 'beta', 'gamma', 'delta'] as const;

function quadAngleWinding(vertices: JXGElement[]) {
  return [
    { a: vertices[1], vertex: vertices[0], b: vertices[3] },
    { a: vertices[2], vertex: vertices[1], b: vertices[0] },
    { a: vertices[3], vertex: vertices[2], b: vertices[1] },
    { a: vertices[0], vertex: vertices[3], b: vertices[2] },
  ];
}

/**
 * Create right-angle markers for shapes where all angles are 90°
 * (rectangle, square). No labels, square markers, fixed radius.
 *
 * Pushes results directly into ctx.jsxObjects.
 * Returns the 4 angle JSXGraph elements for namedObjects registration.
 */
export function createRightAngleMarkers(
  vertices: JXGElement[],
  shapeType: string,
  ctx: ShapeCreatorContext,
): JXGElement[] {
  const { elementFactory, geometryConfig, elementDisplay, styleCtx, jsxObjects } = ctx;
  const rightAngleDisplay = geometryConfig.angle?.rightAngleDisplay ?? 'square';
  const rightAngleType = rightAngleDisplay === 'circle-dot' ? 'sector' : ('square' as const);
  const winding = quadAngleWinding(vertices);
  const angleElements: JXGElement[] = [];

  for (let i = 0; i < 4; i++) {
    const angleKey = QUAD_ANGLE_KEYS[i];
    const angleStyle = resolveGeometryStyle('angle', shapeType, styleCtx, elementDisplay, angleKey);
    const explicitVisible = elementDisplay?.subparts?.[angleKey]?.visible;

    const angleResult = elementFactory.angle({
      a: winding[i].a,
      vertex: winding[i].vertex,
      b: winding[i].b,
      name: angleKey,
      style: {
        fillColor: angleStyle.fillColor,
        fillOpacity: angleStyle.fillOpacity,
        strokeColor: angleStyle.strokeColor,
        type: rightAngleType,
        radius: 0.4,
        visible: explicitVisible ?? angleStyle.visible ?? true,
        label: false,
      },
    });
    angleElements.push(angleResult.angle);
    jsxObjects.push(angleResult.angle);
    jsxObjects.push(...angleResult.helpers);
  }

  return angleElements;
}

/**
 * Create general sector angle markers for quadrilateral shapes
 * (parallelogram, rhombus, trapezoid). Supports label deduplication.
 *
 * Pushes results directly into ctx.jsxObjects and ctx.labels.
 * Returns the 4 angle JSXGraph elements for namedObjects registration.
 *
 * @param labeledCount How many of the 4 angles get labels.
 *   - 2 for shapes with opposite equal angles (parallelogram, rhombus)
 *   - 4 for shapes with all unique angles (trapezoid)
 */
export function createGeneralAngleMarkers(
  vertices: JXGElement[],
  shapeType: string,
  element: GeometryElement,
  givenKeys: Set<string>,
  ctx: ShapeCreatorContext,
  labeledCount: number,
): JXGElement[] {
  const { elementFactory, geometryConfig, elementDisplay, styleCtx, jsxObjects, labels } = ctx;
  const winding = quadAngleWinding(vertices);
  const angleName = (element as GeometryElement & { names?: Record<string, string> }).names?.angle ?? 'angle';
  const angleElements: JXGElement[] = [];

  for (let i = 0; i < 4; i++) {
    const angleKey = QUAD_ANGLE_KEYS[i];

    const angleStyle = resolveGeometryStyle('angle', shapeType, styleCtx, elementDisplay, angleKey);
    const angleLabelColor = resolveGeometryLabelColor(
      shapeType,
      styleCtx,
      elementDisplay,
      angleKey,
      'angle',
    ) as ThemeColorName;

    const scopeResult = computeAngleScope({ prop: 'angle' }, givenKeys, geometryConfig, angleLabelColor, angleName);

    const explicitVisible = elementDisplay?.subparts?.[angleKey]?.visible;
    const visible = explicitVisible ?? angleStyle.visible ?? true;

    const showLabel = i < labeledCount;
    const scopeLabel = showLabel ? scopeResult.label : false;
    const label = resolveAngleLabelOverride(angleStyle.label, scopeLabel, angleName, angleLabelColor);

    const angleResult = elementFactory.angle({
      a: winding[i].a,
      vertex: winding[i].vertex,
      b: winding[i].b,
      name: i === 0 ? angleName : angleKey,
      style: {
        fillColor: angleStyle.fillColor,
        fillOpacity: angleStyle.fillOpacity,
        strokeColor: angleStyle.strokeColor,
        type: 'sector',
        radius: angleStyle.radius,
        visible,
        label,
      },
    });
    angleElements.push(angleResult.angle);
    jsxObjects.push(angleResult.angle);
    jsxObjects.push(...angleResult.helpers);
    labels.push(angleResult.label);
  }

  return angleElements;
}

/**
 * Apply per-edge visibility overrides from the display config.
 * Hides individual polygon borders when `subparts[edgeName].visible === false`.
 */
export function applyEdgeVisibilityOverrides(
  borders: JXGElement[],
  edgeNames: readonly string[],
  elementDisplay: GeometryElementDisplayConfig | undefined,
): void {
  if (!elementDisplay?.subparts) {
    return;
  }
  for (let i = 0; i < edgeNames.length; i++) {
    if (elementDisplay.subparts[edgeNames[i]]?.visible === false) {
      (borders[i] as unknown as { setAttribute: (attrs: Record<string, unknown>) => void }).setAttribute({
        visible: false,
      });
    }
  }
}

/**
 * Resolve base vertex style from shape theme + display config.
 * Merges hardcoded defaults with the shape theme's root-level point config
 * and any per-element display overrides.
 *
 * Used as the `style.vertex` parameter in polygon factory calls.
 */
export function resolveBaseVertexStyle(shapeType: string, ctx: ShapeCreatorContext): PointStyle {
  const base: PointStyle = { fixed: true, label: false };
  const shapePoint = ctx.styleCtx.shapeTheme[shapeType]?.point;
  const displayPoint = ctx.elementDisplay?.point;
  return { ...base, ...(shapePoint as Partial<PointStyle>), ...(displayPoint as Partial<PointStyle>) };
}

/**
 * Build per-vertex style overrides for the polygon factory's `vertexPerIndex`.
 * Merges shape theme subpart colors with display config overrides
 * and explicit visibility toggles from the editor.
 *
 * Analogous to `borderPerEdge` for edges — the polygon factory handles
 * color resolution internally via its themeService.
 */
export function buildVertexPerIndex(
  vertexNames: readonly string[],
  shapeType: string,
  ctx: ShapeCreatorContext,
): Record<number, PointStyle> | undefined {
  const { elementDisplay, styleCtx } = ctx;
  const result: Record<number, PointStyle> = {};
  let hasOverrides = false;

  for (let i = 0; i < vertexNames.length; i++) {
    const name = vertexNames[i];
    const themeStyle = styleCtx.shapeTheme[shapeType]?.subparts?.[name]?.point;
    const displayStyle = elementDisplay?.subparts?.[name]?.point;
    const explicitVisible = elementDisplay?.subparts?.[name]?.visible;

    if (themeStyle || displayStyle || explicitVisible !== undefined) {
      hasOverrides = true;
      const entry: PointStyle = {
        ...(themeStyle as Partial<PointStyle>),
        ...(displayStyle as Partial<PointStyle>),
      };
      if (explicitVisible !== undefined) {
        entry.visible = explicitVisible;
      }
      result[i] = entry;
    }
  }

  return hasOverrides ? result : undefined;
}
